import { useState, useEffect } from 'react';
import axios_instance from '../api/axios_instance';
import {
  TrendingUp, Clock, PackageCheck,
  Users, DollarSign, Plus, Loader2
} from 'lucide-react';
import { Link } from 'react-router-dom';

const StatCard = ({ title, value, icon: Icon, color, subValue }) => (
  <div className="bg-white border border-gray-400 rounded-[2.5rem] p-8 flex flex-col justify-between h-full group hover:border-gray-900 transition-all">
    <div className="flex justify-between items-start">
      <div className={`p-4 rounded-2xl border border-gray-400 ${color} text-gray-900`}>
        <Icon size={24} />
      </div>
      <div className="text-left">
        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest border border-gray-200 px-2 py-1 rounded-lg">Real-time</span>
      </div>
    </div>
    <div className="mt-8">
      <h3 className="text-xs font-black text-gray-400 uppercase mb-2">{title}</h3>
      <p className="text-4xl font-black text-gray-900 tracking-tighter">{value}</p>
      {subValue && <p className="text-xs font-bold text-emerald-600 mt-2 flex items-center gap-1">{subValue}</p>}
    </div>
  </div>
);

const DashboardHome = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getStats = async () => {
      try {
        const res = await axios_instance.get('/api/admin/stats-summary');
        setData(res.data);
      } catch (err) {
        console.error("Dashboard Fetch Error", err);
      } finally {
        setLoading(false);
      }
    };
    getStats();
  }, []);

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
      <Loader2 className="animate-spin text-emerald-600" size={48} />
      <p className="font-black text-gray-400 animate-pulse uppercase tracking-widest">تحميل البيانات...</p>
    </div>
  );

  return (
    <div className="space-y-8" dir="rtl">
      {/* 1. Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h2 className="text-4xl font-black text-gray-900 tracking-tighter uppercase">ملخص النشاط</h2>
          <p className="text-gray-500 font-bold mt-1">تتبع المبيعات والمخزون في benmousaherbs.tn</p>
        </div>
        <div className="flex items-center gap-3">
            <div className="text-left ml-4" dir="ltr">
                <p className="text-[10px] font-black text-gray-400 uppercase">Last Update</p>
                <p className="text-xs font-black text-gray-900">{new Date().toLocaleTimeString()}</p>
            </div>
            <Link to="/admin/addProduct" className="bg-emerald-600 text-white p-4 rounded-2xl border-none hover:bg-emerald-700 transition-all">
                <Plus size={24} />
            </Link>
        </div>
      </div>

      {/* 2. Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="الأرباح المحققة"
          value={`${data?.revenue?.toFixed(3) || "0.000"} DT`}
          icon={DollarSign}
          color="bg-emerald-100"
          subValue={`من ${data?.deliveredOrders || 0} طلبية مسلّمة`}
        />
        <StatCard
          title="طلبيات جديدة"
          value={data?.pendingOrders || 0}
          icon={Clock}
          color="bg-orange-100"
          subValue="تحتاج مراجعة فورية"
        />
        <StatCard
          title="إجمالي الطلبيات"
          value={data?.totalOrders || 0}
          icon={Users}
          color="bg-purple-100"
          subValue="منذ بداية النشاط"
        />
        <StatCard
          title="منتجات نشطة"
          value={data?.activeProducts || 0}
          icon={PackageCheck}
          color="bg-blue-100"
          subValue="معروضة حالياً بالمتجر"
        />
      </div>

      {/* 3. Detailed Insights Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Recent Orders List */}
        <div className="lg:col-span-2 bg-white border border-gray-400 rounded-[2.5rem] p-8">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
              <Users size={20} className="text-emerald-600" /> آخر النشاطات
            </h3>
            <Link to="/admin/orders" className="text-xs font-black text-gray-400 border border-gray-200 px-4 py-2 rounded-xl hover:bg-gray-50 transition-all">
              عرض كل الطلبيات
            </Link>
          </div>

          <div className="space-y-4">
            {data?.recentOrders?.map((order) => (
              <div key={order._id} className="flex items-center justify-between p-5 border border-gray-200 rounded-2xl hover:border-gray-400 transition-all">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center font-black text-gray-400 border border-gray-200 text-sm">
                    {order.customer.firstName?.[0] || order.customer.phone?.slice(-2) || '#'}
                  </div>
                  <div>
                    <p className="font-black text-gray-900">
                      {order.customer.firstName || order.customer.lastName
                        ? `${order.customer.firstName} ${order.customer.lastName}`.trim()
                        : order.customer.phone}
                    </p>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{order.customer.city || '—'} • {new Date(order.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="text-left">
                  <p className="font-black text-gray-900">{order.totalPrice.toFixed(3)} DT</p>
                  <span className={`text-[8px] font-black px-2 py-0.5 rounded border uppercase ${
                    order.status === 'Pending' ? 'bg-orange-50 border-orange-200 text-orange-600' : 'bg-emerald-50 border-emerald-200 text-emerald-600'
                  }`}>
                    {order.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Products / Inventory Insights */}
        <div className="space-y-8">
          <div className="bg-gray-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden">
            <h3 className="text-lg font-black mb-6 flex items-center gap-2">
              <TrendingUp size={20} className="text-emerald-400" /> الأكثر مبيعاً
            </h3>
            <div className="space-y-6 relative z-10">
              {data?.topProducts?.map((prod, idx) => (
                <div key={idx} className="flex items-center justify-between border-b border-gray-800 pb-4 last:border-none last:pb-0">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-black text-gray-600">{idx + 1}</span>
                    <p className="text-sm font-bold text-gray-200">{prod.name.ar}</p>
                  </div>
                  <p className="text-xs font-black text-emerald-400">{prod.salesCount} مبيع</p>
                </div>
              ))}
            </div>
            <TrendingUp size={120} className="absolute -bottom-6 -left-6 text-white/5 rotate-12" />
          </div>

          <div className="bg-white border border-gray-400 rounded-[2.5rem] p-8 space-y-4">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">متوسط الطلب</h3>
            <p className="text-3xl font-black text-gray-900">{data?.avgOrderValue?.toFixed(3) || "0.000"} DT</p>
            <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
              <div className="bg-emerald-600 h-full w-[70%]" />
            </div>
            <p className="text-[10px] text-gray-400 font-bold">يتحسن أداء السلة الشرائية بنسبة 5% هذا الأسبوع</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;